import React, { useEffect } from 'react';
import { WorkflowAssignment, TeamMember, Workflow, Asset } from '../../types';
import { useZodForm, useToast } from '../../hooks';
import { copyAssignmentSchema, CopyAssignmentFormData } from '../../schemas';
import Button from '../ui/Button';

interface CopyAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCopy: (data: { workflowId: string; workerId: string; assetId?: string; dueDate?: string }) => Promise<void>;
  assignment: WorkflowAssignment | null;
  teamMembers: TeamMember[];
  workflows: Workflow[];
  assets: Asset[];
}

const inputClasses = "w-full h-10 px-3 bg-neutral-100 rounded-md text-sm text-neutral-900 border border-neutral-200 focus:border-neutral-400 focus:outline-none";
const labelClasses = "block text-xs text-neutral-500 mb-1";

const CopyAssignmentModal: React.FC<CopyAssignmentModalProps> = ({
  isOpen,
  onClose,
  onCopy,
  assignment,
  teamMembers,
  assets,
}) => {
  const toast = useToast();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isValid },
  } = useZodForm<CopyAssignmentFormData>(copyAssignmentSchema, {
    defaultValues: { workflowId: '', workerId: '', dueDate: '' },
    mode: 'onBlur',
    shouldFocusError: false,
  });

  useEffect(() => {
    if (assignment) {
      reset({ workflowId: assignment.workflowId, workerId: '', dueDate: '' });
    }
  }, [assignment, reset]);

  if (!isOpen || !assignment) return null;

  const onSubmit = async (data: CopyAssignmentFormData) => {
    try {
      const formData = data as any;
      await onCopy({
        workflowId: assignment.workflowId,
        workerId: data.workerId,
        assetId: formData.assetId || undefined,
        dueDate: data.dueDate || undefined,
      });
      onClose();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to create assignment');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-surface rounded-xl shadow-2xl border border-neutral-200 w-full max-w-xs mx-4">
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <div className="p-5 space-y-4">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-sm font-semibold text-neutral-900">Duplicate Assignment</h2>
              <button
                type="button"
                onClick={onClose}
                className="text-neutral-400 hover:text-neutral-600 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div>
              <label className={labelClasses}>Assign To</label>
              <select {...register('workerId')} className={inputClasses}>
                <option value="">Select...</option>
                {teamMembers.map(m => (
                  <option key={m.id} value={m.id}>{m.fullName}</option>
                ))}
              </select>
              {errors.workerId && <p className="text-xs text-danger-500 mt-1">{errors.workerId.message}</p>}
            </div>

            {assets.length > 0 && (
              <div>
                <label className={labelClasses}>Asset</label>
                <select {...register('assetId' as any)} className={inputClasses}>
                  <option value="">Same as original</option>
                  {assets.map(a => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className={labelClasses}>Due Date</label>
              <input
                type="date"
                {...register('dueDate')}
                min={new Date().toISOString().split('T')[0]}
                className={inputClasses}
              />
            </div>
          </div>

          <div className="px-5 pb-5 flex justify-end gap-2">
            <Button type="button" variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="primary" size="sm" disabled={isSubmitting || !isValid} loading={isSubmitting}>
              Duplicate
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CopyAssignmentModal;
